import React from 'react';
import { FaExclamationTriangle, FaCheckCircle, FaShieldAlt, FaPrint, FaHistory } from 'react-icons/fa';
import { Modal, ModalFooter } from './Modals';
import { printAssessmentReport, impactAssessmentReportHTML } from '../utils/assessmentReports';
import { impactAssessmentAPI } from '../services/api';
import VersionHistoryModal from './VersionHistoryModal';

export const CONTAMINATION_METHOD_OPTIONS = [
  'Toxicity / PDE-based',
  'Therapeutic Dose-based',
  'Solubility-based',
  'Cleanability-based',
  'Combined / Weighted Worst-Case Ranking',
  'Not Applicable',
];

const CV_COLORS = {
  required: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  notRequired: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
};

const fieldLabelStyle = { fontWeight: 700, fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: '6px', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 'var(--fs-base)' };

const OUTCOME_LABELS = {
  covered: { label: 'Covered by existing validation', color: '#15803d', bg: '#dcfce7', border: '#86efac' },
  one_verification_run: { label: 'One verification run', color: '#b45309', bg: '#fef3c7', border: '#fcd34d' },
  full_validation: { label: 'Full validation (3 runs)', color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5' },
};

const BASIS_LABELS = { hbel: 'HBEL (PDE)', dose: 'Therapeutic Dose', ppm: '10 ppm', tox: 'Toxicological (LD50)', detergent: 'Detergent' };

// Worst-case ranking readout, placed first (above the MACO readout) since "does this
// candidate out-rank what's already validated on this train" is the question that decides
// everything else below it. Uses the same 1-3 severity-score buckets shown on Risk Assessment —
// HIGHER score = more severe here (not the Worst Case page's WCR rank number, where LOWER
// is worse) — labeled explicitly so the two numbering conventions are never confused.
const WorstCaseRankPanel = ({ t }) => {
  if (t.first_product_on_train) return null;
  const candidateHigher = t.candidate_score > t.previous_worst_case_score;
  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', background: t.becomes_worst_case ? 'var(--warning-bg, #fef3c7)' : 'var(--input-bg, #f8fafc)' }}>
      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
        Worst-Case Ranking (severity score — higher is more severe)
      </div>
      <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
        <div><strong>Candidate:</strong> {t.candidate_api || '—'} — score {t.candidate_score}</div>
        <div><strong>Current worst case:</strong> {t.previous_worst_case_api || 'none'} — score {t.previous_worst_case_score}</div>
      </div>
      {t.becomes_worst_case && (
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--warning-text, #92400e)', marginTop: '6px', fontWeight: 600 }}>
          ⚠ This candidate out-ranks the current worst case{candidateHigher ? ` (${t.candidate_score} > ${t.previous_worst_case_score})` : ''} — it becomes the new worst case on this train, and the MACO basis changes accordingly.
        </div>
      )}
      {t.color_worst_case_combo && (
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--danger-color, #b91c1c)', marginTop: '6px', fontWeight: 700 }}>
          ⚠ Compounded risk: this worst-case candidate is also a colored product — residue may be harder to catch visually, so don't rely on Visual alone for release; weight analytical (swab/rinse) criteria higher.
        </div>
      )}
    </div>
  );
};

// Governing MACO/MAR readout — the actual number QA needs to set an AMV/VRL target
// against, not just a pass/fail flag. Shows every enabled criterion so it's visible
// whether the governing one really is the lowest (least) value, same convention as
// the MACO Calculator page.
const MacoSummaryPanel = ({ m }) => {
  if (!m) return null;
  const rows = Object.entries(m.criteria_mar_mcg_cm2 || {})
    .filter(([key]) => m.criteria_enabled?.[key])
    .map(([key, mar]) => ({ key, mar, maco: m.criteria_maco_mg?.[key] }));
  if (!rows.length) return null;
  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', background: 'var(--input-bg, #f8fafc)' }}>
      <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>
        Worst-Case / MACO Readout {m.worst_case_api ? `— vs. ${m.worst_case_api}` : ''}
      </div>
      <table style={{ width: '100%', fontSize: 'var(--fs-sm)', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
            <th style={{ padding: '2px 6px' }}>Criterion</th>
            <th style={{ padding: '2px 6px' }}>MACO (mg)</th>
            <th style={{ padding: '2px 6px' }}>MAR (µg/cm²)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isGoverning = r.key === m.governing_basis;
            return (
              <tr key={r.key} style={{ fontWeight: isGoverning ? 700 : 400, color: isGoverning ? 'var(--danger-color, #b91c1c)' : 'inherit' }}>
                <td style={{ padding: '2px 6px' }}>{BASIS_LABELS[r.key] || r.key}{isGoverning ? ' ← governing (least)' : ''}</td>
                <td style={{ padding: '2px 6px' }}>{Number.isFinite(r.maco) ? r.maco.toFixed(4) : '—'}</td>
                <td style={{ padding: '2px 6px' }}>{Number.isFinite(r.mar) ? r.mar.toFixed(4) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '6px' }}>
        Use the governing MAR above as the target sensitivity when setting up the Analytical Method Validation (AMV) and VRL study for this API/line — swab-area-cm² basis, effective area {m.effective_surface_area_cm2 ? Number(m.effective_surface_area_cm2).toFixed(0) : '—'} cm².
      </div>
    </div>
  );
};

const TrainFinding = ({ t }) => {
  const flags = [];
  if (t.first_product_on_train) flags.push('First product on this train — baseline CV required');
  // "Becomes worst case" itself is now shown in WorstCaseRankPanel above, with the actual
  // scores — not repeated here as a bare line.
  if (!t.becomes_worst_case && t.batch_increased) flags.push(`Batch size increased (${t.candidate_batch_size} > ${t.max_existing_batch_size}) — recheck MACO`);
  if (t.uncovered_equipment?.length) flags.push(`${t.uncovered_equipment.length} equipment item(s) without a validated cleaning method: ${t.uncovered_equipment.map((e) => e.equipment_name).join(', ')}`);

  const checklist = t.checklist || [];

  return (
    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' }}>
      <div style={{ fontWeight: 700, fontSize: 'var(--fs-md)', marginBottom: (flags.length || checklist.length || t.maco_summary) ? '6px' : 0 }}>{t.train_name}</div>
      <WorstCaseRankPanel t={t} />
      <MacoSummaryPanel m={t.maco_summary} />
      {flags.length === 0 && checklist.length === 0 ? (
        <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>No worst-case, MACO, or equipment-coverage impact detected.</div>
      ) : (
        <>
          {flags.length > 0 && (
            <ul style={{ margin: '0 0 6px', paddingLeft: '18px', fontSize: 'var(--fs-md)' }}>
              {flags.map((f, i) => <li key={i} style={{ marginBottom: '2px' }}>{f}</li>)}
            </ul>
          )}
          {checklist.length > 0 && (
            <table style={{ width: '100%', fontSize: 'var(--fs-sm)', borderCollapse: 'collapse' }}>
              <tbody>
                {checklist.map((c) => (
                  <tr key={c.check} style={{ borderTop: '1px solid var(--border-light, #eee)' }}>
                    <td style={{ padding: '4px 6px', width: '22px', textAlign: 'center' }}>
                      {c.flag === true ? '⚠️' : c.flag === false ? '✅' : '—'}
                    </td>
                    <td style={{ padding: '4px 6px', fontWeight: 600 }}>{c.label}</td>
                    <td style={{ padding: '4px 6px', color: 'var(--text-secondary)' }}>{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const CAN_DECIDE_ROLES = ['qa', 'qa head', 'qahead', 'qa_head', 'admin', 'super admin'];

const getCurrentRole = () => {
  try {
    return String(JSON.parse(localStorage.getItem('user') || '{}')?.role || '').trim().toLowerCase();
  } catch (error) {
    return '';
  }
};

// Edit documentation / submit for QA / QA approve-return-reject, each signed with the user's password.
// onWorkflow(action, payload) must return a promise; action is 'update' | 'submit' | 'decide'.
const WorkflowPanel = ({ record, onWorkflow }) => {
  const [editing, setEditing] = React.useState(false);
  const [doc, setDoc] = React.useState({
    contamination_method: record.contamination_method || '',
    cross_contamination_note: record.cross_contamination_note || '',
    justification: record.justification || '',
  });
  const [password, setPassword] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const status = record.status || 'Pending Documentation';
  const locked = ['Pending QA Approval', 'Approved'].includes(status);
  const canDecide = CAN_DECIDE_ROLES.includes(getCurrentRole());
  const canSubmit = ['Recorded', 'Returned'].includes(status);
  const signed = { esign_password: password, esign_comment: comment };

  const run = async (action, payload) => {
    setBusy(true);
    try {
      await onWorkflow(action, payload);
      setPassword('');
      setComment('');
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (!locked && !canSubmit && !editing && status !== 'Pending Documentation' && status !== 'Rejected') return null;

  return (
    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {!locked && editing && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <select style={inputStyle} value={doc.contamination_method} onChange={(e) => setDoc({ ...doc, contamination_method: e.target.value })}>
            <option value="">Select method...</option>
            {CONTAMINATION_METHOD_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          <textarea style={{ ...inputStyle, minHeight: '50px' }} placeholder="Cross-contamination risk to subsequent products" value={doc.cross_contamination_note} onChange={(e) => setDoc({ ...doc, cross_contamination_note: e.target.value })} />
          <textarea style={{ ...inputStyle, minHeight: '50px' }} placeholder="Justification / rationale" value={doc.justification} onChange={(e) => setDoc({ ...doc, justification: e.target.value })} />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => run('update', { ...doc, manual_override: !!record.manual_override, override_reason: record.override_reason, cv_required_override: !!record.cv_required })}>Save documentation</button>
          </div>
        </div>
      )}

      {!editing && !locked && (
        <button type="button" className="btn btn-outline btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setEditing(true)}>
          {status === 'Pending Documentation' ? 'Complete documentation' : 'Edit documentation'}
        </button>
      )}

      {!editing && (canSubmit || (status === 'Pending QA Approval' && canDecide)) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <input type="password" style={inputStyle} placeholder="E-signature: your password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          <input style={inputStyle} placeholder={canSubmit ? 'Reason / comment for signature' : 'Comment (this is your signed reason — required for return / reject)'} value={comment} onChange={(e) => setComment(e.target.value)} />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {canSubmit && (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !password || !comment.trim()} onClick={() => run('submit', signed)}>Submit for QA approval</button>
            )}
            {status === 'Pending QA Approval' && (
              <>
                <button type="button" className="btn btn-success btn-sm" disabled={busy || !password || !comment.trim()} onClick={() => run('decide', { ...signed, decision: 'approve' })}>Approve</button>
                <button type="button" className="btn btn-warning btn-sm" disabled={busy || !password || !comment.trim()} onClick={() => run('decide', { ...signed, decision: 'return' })}>Return</button>
                <button type="button" className="btn btn-danger btn-sm" disabled={busy || !password || !comment.trim()} onClick={() => run('decide', { ...signed, decision: 'reject' })}>Reject</button>
              </>
            )}
          </div>
        </div>
      )}
      {status === 'Pending QA Approval' && !canDecide && (
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Waiting for QA approval.</div>
      )}
    </div>
  );
};

/**
 * mode: 'gate' — pre-save screening shown while adding a product; user must complete the
 *   documentation fields (and may manually override the recommendation) before continuing.
 * mode: 'view' — read-only display of an already-recorded assessment.
 */
export const ImpactAssessmentModal = ({
  mode = 'gate',
  loading = false,
  evaluation,
  record,
  values,
  onChange,
  onConfirm,
  onClose,
  onWorkflow,
  title,
}) => {
  const isGate = mode === 'gate';
  const trainResults = isGate ? (evaluation?.trainResults || []) : (record?.train_results || []);
  const systemCvRequired = isGate ? !!evaluation?.systemCvRequired : !!record?.system_cv_required;
  const systemReason = isGate ? evaluation?.systemReason : record?.system_reason;
  const recommendedOutcome = isGate ? evaluation?.recommendedOutcome : record?.recommended_outcome;
  const outcomeReason = isGate ? evaluation?.outcomeReason : record?.outcome_reason;
  const outcomeInfo = OUTCOME_LABELS[recommendedOutcome];

  const manualOverride = isGate ? !!values?.manual_override : !!record?.manual_override;
  const finalCvRequired = manualOverride
    ? (isGate ? !!values?.cv_required_override : !!record?.cv_required)
    : systemCvRequired;

  const colors = finalCvRequired ? CV_COLORS.required : CV_COLORS.notRequired;

  const [historyOpen, setHistoryOpen] = React.useState(false);

  const canConfirm = isGate && !loading &&
    !!values?.contamination_method &&
    !!(values?.cross_contamination_note || '').trim() &&
    !!(values?.justification || '').trim() &&
    (!manualOverride || !!(values?.override_reason || '').trim());

  return (
    <>
    <Modal
      isOpen
      size="md"
      title={<><FaShieldAlt /> {title || 'Cleaning Validation Impact Assessment'}</>}
      onClose={onClose}
      closeButton={!isGate}
      closeOnBackdropClick={!isGate}
    >
          {loading ? (
            <div className="loading-container"><div className="spinner"></div></div>
          ) : (
            <>
              {outcomeInfo && (
                <div style={{ background: outcomeInfo.bg, border: `1.5px solid ${outcomeInfo.border}`, borderRadius: '10px', padding: '10px 16px', marginBottom: '10px' }}>
                  <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: outcomeInfo.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Checklist Outcome</div>
                  <div style={{ fontSize: 'var(--fs-md)', fontWeight: 800, color: outcomeInfo.color }}>{outcomeInfo.label}</div>
                  {outcomeReason && <div style={{ fontSize: 'var(--fs-sm)', color: outcomeInfo.color, marginTop: '2px' }}>{outcomeReason}</div>}
                  {isGate && (
                    <div style={{ fontSize: 'var(--fs-sm)', color: outcomeInfo.color, marginTop: '6px', fontStyle: 'italic' }}>
                      This is a recommendation, not a requirement to save — a brand-new API naturally has no VRL study or validated analytical method yet. Typical sequence: add the API → confirm its MACO/worst-case position → then complete VRL and analytical method work before the actual cleaning validation run. Saving this product now does not require any of that to be finished first.
                    </div>
                  )}
                </div>
              )}
              <div style={{ background: colors.bg, border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                {finalCvRequired ? <FaExclamationTriangle style={{ color: colors.text, marginTop: '2px', flexShrink: 0 }} /> : <FaCheckCircle style={{ color: colors.text, marginTop: '2px', flexShrink: 0 }} />}
                <div>
                  <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {manualOverride ? 'Manually Overridden Outcome' : 'System Recommendation'}
                  </div>
                  <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 800, color: colors.text }}>
                    Cleaning Validation {finalCvRequired ? 'Required' : 'Not Required'}
                  </div>
                  {manualOverride && (
                    <div style={{ fontSize: 'var(--fs-sm)', color: colors.text, marginTop: '2px' }}>
                      System screening said: {systemCvRequired ? 'Required' : 'Not required'}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '12px' }}>{systemReason}</div>

              <div style={{ marginBottom: '16px' }}>
                {trainResults.length === 0 ? (
                  <div style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>No equipment train linked — link one to screen for worst-case/MACO impact.</div>
                ) : trainResults.map((t) => <TrainFinding key={t.train_id} t={t} />)}
              </div>

              {isGate ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: '1px solid var(--border-color)', paddingTop: '14px' }}>
                  <div>
                    <label style={fieldLabelStyle}>Contamination / worst-case determination method (required)</label>
                    <select style={inputStyle} value={values?.contamination_method || ''} onChange={(e) => onChange('contamination_method', e.target.value)}>
                      <option value="">Select method...</option>
                      {CONTAMINATION_METHOD_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={fieldLabelStyle}>Cross-contamination risk to subsequent products (required)</label>
                    <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={values?.cross_contamination_note || ''} onChange={(e) => onChange('cross_contamination_note', e.target.value)} placeholder="Describe the cross-contamination risk to the next product(s) run on this train..." />
                  </div>
                  <div>
                    <label style={fieldLabelStyle}>
                      Justification / rationale (required{!finalCvRequired ? ' — mandatory even though CV is not required' : ''})
                    </label>
                    <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={values?.justification || ''} onChange={(e) => onChange('justification', e.target.value)} placeholder={finalCvRequired ? 'Rationale for requiring cleaning validation...' : 'Rationale for why cleaning validation is not required...'} />
                  </div>

                  <div style={{ background: 'var(--input-bg, #f8fafc)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: 'var(--fs-md)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={manualOverride} onChange={(e) => onChange('manual_override', e.target.checked)} />
                      Manually override the system recommendation
                    </label>
                    {manualOverride && (
                      <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', gap: '16px', fontSize: 'var(--fs-md)' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input type="radio" name="cv_required_override" checked={values?.cv_required_override === true} onChange={() => onChange('cv_required_override', true)} /> CV Required
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input type="radio" name="cv_required_override" checked={values?.cv_required_override === false} onChange={() => onChange('cv_required_override', false)} /> CV Not Required
                          </span>
                        </label>
                        <textarea style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }} value={values?.override_reason || ''} onChange={(e) => onChange('override_reason', e.target.value)} placeholder="Reason for overriding the system recommendation (required)..." />
                      </div>
                    )}
                  </div>

                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid var(--border-color)', paddingTop: '14px', fontSize: 'var(--fs-md)' }}>
                  <div><strong>Determination method:</strong> {record?.contamination_method || '—'}</div>
                  <div><strong>Cross-contamination note:</strong> {record?.cross_contamination_note || '—'}</div>
                  <div><strong>Justification:</strong> {record?.justification || '—'}</div>
                  {record?.manual_override && <div><strong>Override reason:</strong> {record?.override_reason || '—'}</div>}
                  <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
                    Status: {record?.status || 'Pending Documentation'}
                    {record?.created_by_user ? ` · Recorded by ${record.created_by_user}` : ''}
                    {record?.created_at ? ` on ${new Date(record.created_at).toLocaleString()}` : ''}
                  </div>
                  {record?.approval_decision && (
                    <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>
                      QA decision: <strong>{record.approval_decision}</strong>
                      {record.approval_comment ? ` — ${record.approval_comment}` : ''}
                      {record.approved_at ? ` (${new Date(record.approved_at).toLocaleString()})` : ''}
                    </div>
                  )}
                  {onWorkflow && record && (
                    <WorkflowPanel record={record} onWorkflow={onWorkflow} />
                  )}
                </div>
              )}
            </>
          )}
        <ModalFooter>
          {isGate ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-primary" disabled={!canConfirm} onClick={onConfirm}>
                Confirm &amp; Continue to Save
              </button>
            </>
          ) : (
            <>
              {record && (
                <button type="button" className="btn btn-outline" onClick={() => printAssessmentReport({ reportKey: 'impact_assessment',
                  title: `Impact Assessment - ${record.product_code || record.product_name || ''}`,
                  documentName: 'Cleaning Validation Impact Assessment',
                  build: () => impactAssessmentReportHTML(record),
                })}>
                  <FaPrint style={{ marginRight: 4 }} />Print / PDF
                </button>
              )}
              {record?.id && (
                <button type="button" className="btn btn-outline" onClick={() => setHistoryOpen(true)}>
                  <FaHistory style={{ marginRight: 4 }} />History
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
            </>
          )}
        </ModalFooter>
    </Modal>
    {record?.id && (
      <VersionHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`Version History — ${record.product_code || record.product_name || 'Impact Assessment'}`}
        fetcher={() => impactAssessmentAPI.getRevisions(record.id)}
      />
    )}
    </>
  );
};

export default ImpactAssessmentModal;
